import React, { useState } from 'react';
import { MoreHorizontal, User, Calendar } from 'lucide-react';

interface Task {
  id: string;
  title: string;
  assignee: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
}

interface SimpleBoardViewProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const SimpleBoardView: React.FC<SimpleBoardViewProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [tasks] = useState<Record<string, Task[]>>({
    'todo': [
      {
        id: 'task-1',
        title: 'Complete Tyler ERP Setup Documentation',
        assignee: 'Client Team',
        dueDate: '2026-08-30',
        priority: 'high'
      }
    ],
    'in-progress': [
      {
        id: 'task-2',
        title: 'Data Migration Planning',
        assignee: 'Tyler Data Team',
        dueDate: '2026-09-05',
        priority: 'high'
      },
      {
        id: 'task-3',
        title: 'Configure Tyler SSO Integration',
        assignee: 'IT Security Team',
        dueDate: '2026-09-01',
        priority: 'medium'
      }
    ],
    'review': [],
    'done': [
      {
        id: 'task-4',
        title: 'Tyler Module User Training Schedule',
        assignee: 'Tyler Training Coordinator',
        dueDate: '2026-08-25',
        priority: 'low'
      }
    ]
  });

  const columns = [
    { id: 'todo', title: 'To Do', color: 'bg-gray-100 border-gray-300' },
    { id: 'in-progress', title: 'In Progress', color: 'bg-blue-50 border-blue-200' },
    { id: 'review', title: 'Review', color: 'bg-yellow-50 border-yellow-200' },
    { id: 'done', title: 'Done', color: 'bg-green-50 border-green-200' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Project Board - {projectId}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((column) => (
          <div key={column.id} className="flex flex-col">
            <div className={`p-3 rounded-t-lg border-2 ${column.color}`}>
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">{column.title}</h4>
                <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded-full">
                  {tasks[column.id]?.length || 0}
                </span>
              </div>
            </div>
            
            <div className={`flex-1 p-3 border-2 border-t-0 rounded-b-lg min-h-[300px] ${column.color}`}>
              <div className="space-y-3">
                {tasks[column.id]?.map((task) => (
                  <div key={task.id} className="p-3 rounded-lg bg-white border border-gray-200 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                        {task.priority.toUpperCase()}
                      </span>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <h5 className="font-medium text-gray-900 mb-2 text-sm line-clamp-2">
                      {task.title}
                    </h5>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <User className="w-3 h-3" />
                        <span>{task.assignee}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-xs text-gray-500">
        <p>• Drag and drop functionality will be enabled with Control Bridge integration</p>
        <p>• Board view syncs with Work Objects for real-time updates</p>
      </div>
    </div>
  );
};
